import React, {useState, useEffect} from 'react';
import {inject, observer} from 'mobx-react';
import LearnersStore from '../../store/LearnersStore';
import CreateLearnersUI from '../view/CreateLearnersUI';
import {useNavigate, useParams} from 'react-router-dom';
import NavigationStore from '../../../../navigation/store/NavigationStore';
import BulkImportPreviewModal from '../components/BulkImportPreviewModal';
import {parseFile, ParsedLearner} from '../../../../utils/bulkImportParser';

interface Course {
  id: string;
  title: string;
}

interface Props {
  learnersStore?: LearnersStore;
  navigationStore?: NavigationStore;
}

const CreateLearnersContainer = inject(
  'learnersStore',
  'navigationStore',
)(
  observer(({learnersStore, navigationStore}: Props) => {
    const [learners, setLearners] = useState<string[]>([]);
    const [failedInvitations, setFailedInvitations] = useState<any>({});
    const {organizationId} = useParams();
    const navigate = useNavigate();

    // Bulk import preview modal state
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [parsedLearners, setParsedLearners] = useState<ParsedLearner[]>([]);
    const [parseErrors, setParseErrors] = useState<string[]>([]);
    const [unmappedColumns, setUnmappedColumns] = useState<string[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_pendingFile, setPendingFile] = useState<File | null>(null);
    const [courses, setCourses] = useState<Course[]>([]);
    const [isParsing, setIsParsing] = useState(false);

    // Load courses when component mounts
    useEffect(() => {
      if (organizationId) {
        learnersStore!.getOrganizationCourses(organizationId).then(data => {
          setCourses(data || []);
        });
      }
    }, [organizationId, learnersStore]);

    const onSubmit = async (learners: string[]) => {
      if (organizationId) {
        setFailedInvitations({});
        await learnersStore!
          .addLearners(organizationId, learners)
          .then(result => {
            setLearners([]);
            if (
              result.data?.result &&
              Object.keys(result.data?.result.failedReasons).length
            ) {
              setFailedInvitations(result.data?.result.failedReasons);
            } else if (result.success) {
              navigationStore!.getOrganizationDashboardAnalytics(
                organizationId,
              );
              setLearners([]);
              navigate(-1);
            }
          });
      }
    };

    const onCancel = () => {
      navigate(`/manage/${organizationId}/learners`, {replace: true});
    };

    const onFileUpload = async (file: File) => {
      if (organizationId) {
        setIsParsing(true);
        setPendingFile(file);

        try {
          const result = await parseFile(file);
          setParsedLearners(result.learners);
          setParseErrors(result.errors);
          setUnmappedColumns(result.unmappedColumns);
          setShowPreviewModal(true);
        } catch {
          setParseErrors(['Failed to parse file']);
          setShowPreviewModal(true);
        } finally {
          setIsParsing(false);
        }
      }
    };

    const onClosePreviewModal = () => {
      setShowPreviewModal(false);
      setParsedLearners([]);
      setParseErrors([]);
      setUnmappedColumns([]);
      setPendingFile(null);
    };

    // Create a new CSV file from selected learners in the correct format
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _createCsvFromLearners = (
      selectedLearners: ParsedLearner[],
    ): File => {
      // Escape CSV field if it contains comma, quote, or newline
      const escapeField = (field: string): string => {
        if (
          field.includes(',') ||
          field.includes('"') ||
          field.includes('\n')
        ) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      };

      // Build CSV content with header matching the template
      const header = 'FirstName,LastName,Email';
      const rows = selectedLearners.map(learner => {
        const fn = escapeField(learner.firstName);
        const ln = escapeField(learner.lastName);
        const em = escapeField(learner.email);
        return `${fn},${ln},${em}`;
      });
      const csvContent = [header, ...rows].join('\n');

      // Create a new File object
      const blob = new Blob([csvContent], {type: 'text/csv'});
      return new File([blob], 'learners-import.csv', {type: 'text/csv'});
    };

    const onConfirmImport = async (
      selectedLearners: ParsedLearner[],
      selectedCourseIds: string[],
    ) => {
      if (organizationId && selectedLearners.length > 0) {
        setFailedInvitations({});
        const csvFile = _createCsvFromLearners(selectedLearners);
        const result = await learnersStore!.bulkAddLearners(
          organizationId,
          csvFile,
          selectedCourseIds.length > 0 ? selectedCourseIds : undefined,
        );
        if (
          result.data?.result &&
          Object.keys(result.data?.result.failedReasons).length
        ) {
          setFailedInvitations(result.data?.result.failedReasons);
          onClosePreviewModal();
        } else if (result.success) {
          navigationStore!.getOrganizationDashboardAnalytics(organizationId);
          onClosePreviewModal();
          navigate(`/manage/${organizationId}/learners`, {replace: true});
        }
      }
    };

    return (
      <>
        <CreateLearnersUI
          onSubmit={onSubmit}
          onCancel={onCancel}
          onFileUpload={onFileUpload}
          learners={learners}
          setLearners={setLearners}
          isLoading={learnersStore!.isLoading || isParsing}
          failedInvitations={failedInvitations}
        />
        <BulkImportPreviewModal
          isOpen={showPreviewModal}
          onClose={onClosePreviewModal}
          learners={parsedLearners}
          errors={parseErrors}
          unmappedColumns={unmappedColumns}
          onConfirm={onConfirmImport}
          courses={courses}
          isLoading={learnersStore!.isLoading}
        />
      </>
    );
  }),
);

export default CreateLearnersContainer;
